import * as Collections from '@pkg/utils/collections';
import get from '@pkg/utils/objects/get';
import { DesignEntity, Property, Visibility } from '@/lib/enums';
import { compare } from '../../shared/filters/compare';

function getIdentifierValue(entity, condition) {
  if (condition.metricProperty) {
    const propertyPath = `${condition.metricProperty}.${condition.identifier}`;
    return get(entity, propertyPath);
  }

  return condition.identifier.startsWith(Property.PROPERTIES)
    ? getPropertyValue(entity[Property.PROPERTIES], condition)
    : get(entity, condition.identifier);
}

function getPropertyValue(array, condition) {
  const [, propertyId] = condition.identifier.split(':');
  const keyedArray = Collections.keyBy(array);
  return keyedArray[propertyId]?.value;
}

/**
 * Ensures we only include non-default activity conditions.
 *
 * @param {Array} conditions
 *
 * @return {Boolean}
 */
function includesActivityConditions(conditions) {
  if (!conditions.length) {
    return;
  }

  const index = conditions.findIndex((condition) => {
    return (
      condition.identifier !== 'disabled_at' ||
      (condition.identifier === 'disabled_at' && condition.value)
    );
  });

  return index >= 0;
}

export default function apply(snapshot, conditions = {}, peopleSkillMap) {
  if (!snapshot?.entities || typeof snapshot.entities !== 'object') {
    return;
  }

  console.time('Snapshots.filters.apply');
  const filtered = { ...snapshot };

  const filteredSet = {
    groups: new Set(),
    roles: new Set(),
    activities: new Set(),
    people: new Set(),
  };

  const filteredMap = {
    groups: {},
    roles: {},
    activities: {},
    people: {} /** @todo implement */,
  };

  const roleActivitiesMap = {};
  const groupRolesMap = {};

  const entities = filtered.entities;
  const entityOrder = ['groups', 'people', 'roles', 'activities'];
  const entityConditions = conditions;

  // Set up initial filtered set (run through conditions)
  console.time('Snapshots.filters.initial');
  entityOrder.forEach((entityKey) => {
    const singleEntities = Object.values(entities[entityKey]);
    singleEntities.forEach((entity) => {
      const mutableEntity = { ...entity };
      filteredMap[entityKey][entity.uuid] = mutableEntity;

      // # perf - don't loop through roles whose groups and people are already
      // filtered
      if (entityKey === 'roles') {
        if (
          conditions.people?.length > 0 &&
          !filteredSet.people.has(entity.person_uuid)
        ) {
          return;
        }
        const groupExists = Boolean(entities.groups[entity.group_uuid]);
        if (groupExists && !filteredSet.groups.has(entity.group_uuid)) {
          return;
        }
        if (conditions.groups?.length > 0 && !entity?.group_uuid) {
          return;
        }
      }

      if (entityKey === 'people' && peopleSkillMap?.size) {
        const skills = peopleSkillMap?.get(entity.uuid);
        if (skills?.size) {
          mutableEntity.skills = [...skills.entries()].map(([uuid, level]) => {
            return { uuid, level };
          });
        }
      }

      if (
        entityKey === 'activities' &&
        entity.owner_type === DesignEntity.ROLE &&
        !filteredSet.roles.has(entity.owner_uuid)
      ) {
        return;
      }

      // #perf - if one fails, all failed (faster than every)
      const failed = entityConditions[entityKey].some((c) => {
        const identifierValue = getIdentifierValue(mutableEntity, c);
        return !compare(c.relative, identifierValue, c.value);
      });

      if (failed) {
        return;
      }

      //  construct a map of role activities and group roles for later usage
      if (entityKey === 'groups') {
        groupRolesMap[entity.uuid] = [];
      }

      if (entityKey === 'roles') {
        roleActivitiesMap[entity.uuid] = [];
        groupRolesMap[entity.group_uuid]?.push(entity);
      }

      if (
        entityKey === 'activities' &&
        entity.owner_type === DesignEntity.ROLE
      ) {
        roleActivitiesMap[entity.owner_uuid].push(entity);
      }

      filteredSet[entityKey].add(entity.uuid);
      filtered.entities[entityKey][entity.uuid] = entity;
    });
  });
  console.timeEnd('Snapshots.filters.initial');

  console.time('Snapshots.filters.iterate');
  const hasGroupConditions = entityConditions.groups?.length;
  const hasPeopleConditions = entityConditions.people?.length;
  const hasRoleConditions = entityConditions.roles?.length;
  const hasActivityConditions = includesActivityConditions(
    entityConditions.activities
  );

  // clean up filtered set by removing parents/children that are filtered
  filteredSet.activities.forEach((a) => {
    const isRoleOwner = a.owner_type === DesignEntity.ROLE;
    // remove activities inside of non filtered roles
    const ownerId = filteredMap.activities[a].owner_uuid;
    if (isRoleOwner && !filteredSet.roles.has(ownerId)) {
      filteredSet.activities.delete(a);
    }
    // Remove activities inside of non filtered groups.
    if (isRoleOwner) {
      const activityGroupId = filteredMap.roles[ownerId].group_uuid;
      if (activityGroupId && !filteredSet.groups.has(activityGroupId)) {
        filteredSet.activities.delete(a);
      }
    }
  });

  filteredSet.roles.forEach((r) => {
    // remove roles inside of non filtered groups
    const roleGroupUuid = filteredMap.roles[r].group_uuid;
    if (
      roleGroupUuid &&
      hasGroupConditions &&
      !filteredSet.groups.has(roleGroupUuid)
    ) {
      filteredSet.roles.delete(r);
    }

    // remove roles with no activities if activity condition set
    const roleActivities = roleActivitiesMap[r];
    if (roleActivities.length === 0 && hasActivityConditions) {
      filteredSet.roles.delete(r);
    }

    // remove roles that have no filtered activities
    const valid = roleActivities.some(
      (a) => a.owner_uuid === r && filteredSet.activities.has(a.uuid)
    );
    if (!valid && roleActivities.length > 0) {
      filteredSet.roles.delete(r);
    }
  });

  filteredSet.groups.forEach((g) => {
    // remove groups with no roles if role or activity conditions set
    const groupRoles = groupRolesMap[g];
    if (
      groupRoles.length === 0 &&
      (hasPeopleConditions || hasRoleConditions || hasActivityConditions)
    ) {
      filteredSet.groups.delete(g);
    }

    // remove groups that have no filtered roles
    const valid = groupRoles.some(
      (r) => r.group_uuid === g && filteredSet.roles.has(r.uuid)
    );

    if (!valid && groupRoles.length > 0) {
      filteredSet.groups.delete(g);
    }
  });

  // Set the visibility property for all filtered entities
  for (const [entityKey, singleEntities] of Object.entries(entities)) {
    for (const entity of Object.values(singleEntities)) {
      entity.__visibility = filteredSet[entityKey].has(entity.uuid)
        ? Visibility.FULL
        : Visibility.NONE;
    }
  }
  console.timeEnd('Snapshots.filters.iterate');

  /** @todo base snapshot visibility off something else */
  filtered.__visibility = Visibility.FULL;
  console.timeEnd('Snapshots.filters.apply');
  return filtered;
}
